package com.oasisfeng.invaluable.p2p.core

import com.oasisfeng.invaluable.data.LinkProjection
import com.oasisfeng.invaluable.data.LinkProjectionScheme
import com.oasisfeng.invaluable.data.Observation
import com.oasisfeng.invaluable.data.ProjectedLinkRecord
import com.oasisfeng.invaluable.data.ProjectedLinkRecordResolver
import com.oasisfeng.invaluable.data.project
import com.oasisfeng.invaluable.data.renderProjection
import com.oasisfeng.invaluable.data.resolveLink
import com.oasisfeng.invaluable.event.BatchValidationResult
import com.oasisfeng.invaluable.event.BatchWireCodec
import com.oasisfeng.invaluable.event.LinkPreimage
import com.oasisfeng.invaluable.event.ReplicatedEvent
import com.oasisfeng.invaluable.event.ReplicatedEventBatch
import com.oasisfeng.invaluable.event.ReplicatedEventStore
import com.oasisfeng.invaluable.event.ReplicatedEventWireLimits
import com.oasisfeng.invaluable.event.SealedContent
import com.oasisfeng.invaluable.event.SignedCommitment
import com.oasisfeng.invaluable.event.validateReplicatedEventBatch

/**
 * Delta-chain sync for the two replicated object sets ([com.oasisfeng.invaluable.event.SignedCommitment] /
 * [com.oasisfeng.invaluable.event.LinkPreimage]).
 *
 * This is the efficient incremental replacement for app-social's naive full-state push: instead of advertising every
 * projection it holds, a node advertises only its **per-actor commitment frontier** — one head projection per credential
 * (`HEADS`). On receiving a peer's frontier, a node computes exactly what that peer lacks and pushes only the missing
 * **chain suffix**, leaning on three commitment-chain facts:
 *
 * - a commitment's projection content-addresses its whole ancestry, so its parent link points at the previous commitment;
 * - therefore a chain can be walked back from any head via [com.oasisfeng.invaluable.data.ProjectedLinkRecord.parent];
 * - the parent of the first commitment is the actor-log root (it carries no attestation), which marks the chain's genesis.
 *
 * So the responder walks from each of its heads toward the peer's known head, collecting the commitments in between, and
 * streams just those (preimages first, then attestations). With a linear chain this is one round trip and zero
 * redundancy. Forks (an out-of-MVP-scope equivocation) degrade gracefully: when the peer's head is not an ancestor of
 * ours, the walk reaches genesis, and we push the whole chain, which the peer deduplicates by content address.
 *
 * Live authoring is gossiped directly through [publishLocal]; relayed events that advance a peer are forwarded onward so
 * a chain A↔B↔C converges. The sync is transport-agnostic ([P2pSession]) and domain-agnostic (it never names Post/Route).
 *
 * Not thread-safe: confine to one thread or synchronize externally.
 */

class ReplicatedEventSync(
    private val store: ReplicatedEventStore,
    /**
     * Intake for a batch this sync has already validated against [store]: commits the given net-new events and returns
     * those that genuinely entered, so the owner can react to arrivals (see [ReplicatedEventNode.commitValidated]).
     *
     * Takes the whole batch rather than one event at a time because validation has already judged each of them; handing
     * them back to a per-event applier would verify every signature a second time.
     */
    private val commitValidated: (BatchValidationResult.Valid) -> List<ReplicatedEvent>,
    /** Fired whenever a received event advanced local state, so the owner can persist and re-render. */
    private val onAdvanced: () -> Unit = {},
    private val log: (String) -> Unit = {},
    /**
     * Selective reveal (roadmap §7.1): a committed-content projection the owner is withholding from peers. Such a record
     * lives in the local store (so the author still sees its body) but must not enter any catch-up delta — its commitment
     * header still replicates, so peers see a missing observation until the owner reveals it. Live gossip already only
     * carries what [publishLocal] is handed (the owner sends a dark header for a withheld post), so the catch-up walk is
     * the one path that re-derives content from the store and therefore the one that must honour this. Default never
     * withholds, so a node without private posts replicates exactly as before.
     */
    private val isWithheld: (LinkProjection) -> Boolean = { false },
    /**
     * Notified with each [DirectedEnvelope] this node sees, including ones addressed to other peers (a relay forwards
     * without reading). The owner decides whether it is the addressee and, if so, opens the wrap with its own identity
     * — disclosure = forwarding (§20.3, KTD4). Default ignores envelopes, so a node that never discloses is unaffected.
     */
    private val onEnvelope: (DirectedEnvelope) -> Unit = {},
    /**
     * Fired only after the peer has sent every catch-up batch preceding its ordered [SyncControlWire.SYNCED_TAG]
     * barrier. Socket-open is deliberately insufficient: an actionable host may use this signal to stop failing closed
     * on stale local state.
     */
    private val onCaughtUp: (P2pSession) -> Unit = {},
) {
    private val scheme: LinkProjectionScheme = store.projector.scheme
    private val codec = BatchWireCodec(store.projector)
    private val sessions = mutableListOf<P2pSession>()
    private val stagers = mutableMapOf<P2pSession, BatchWireCodec.Stager>()

    // Bounded loop-prevention for directed envelopes: they are not stored, so (unlike replicated events) they cannot
    // dedup via the store. Keyed by the raw line; insertion-ordered so eviction is oldest-first.
    private val seenEnvelopes = LinkedHashSet<String>()

    /** Wire a freshly opened session: receive its lines, drop it on close, and send our frontier digest. */
    fun onSessionOpened(session: P2pSession) = attachSession(session, announceFrontier = true)

    /**
     * Restore a transport session that remained connected while its host runtime slept.
     *
     * The listener and forwarding membership are process-local and must be rebuilt, but the transport did not reopen,
     * so its peer already has the frontier previously announced on this connection. Suppressing a redundant frontier
     * keeps one hibernation wake from triggering O(connected peers) catch-up queries.
     */
    fun onSessionRestored(session: P2pSession) = attachSession(session, announceFrontier = false)

    private fun attachSession(session: P2pSession, announceFrontier: Boolean) {
        sessions += session
        stagers[session] = codec.Stager()
        session.listener = object : P2pSession.Listener {
            override val remotePeerId: String? = null
            override fun onLine(line: String) = receive(session, line)
            override fun onClosed() {
                sessions.remove(session)
                stagers.remove(session)?.discard()
            }
        }
        if (announceFrontier) session.sendRaw(SyncControlWire.encodeFrontier(store.frontier()))
    }

    /** Push locally authored events to every peer (live gossip). The owner calls this after an authoring action. */
    fun publishLocal(events: Iterable<ReplicatedEvent>) {
        val batches = codec.encode(events, store.resolver())
        for (session in sessions.toList()) for (batch in batches) sendBatch(session, batch)
    }

    /**
     * Emit a directed disclosure [envelope] toward its addressee (best-effort flood; relays forward without reading).
     * Marked seen first so a triangle-mesh echo back to us is dropped rather than re-flooded.
     */
    fun sendEnvelope(envelope: DirectedEnvelope) {
        val line = envelope.encode()
        markSeenEnvelope(line)
        for (session in sessions.toList()) session.sendRaw(line)
    }

    /**
     * Explicitly re-fetch the content named by [projection] from peers (KTD7's WANT-by-digest pull). Unlike the frontier
     * delta — which skips any closure below a head the peer already has — this re-requests one specific node by its
     * content address, so a node that **forgot** a body/seal (local forgetting, M10) yet still holds the attributing
     * head can recover it. Serving the request is simultaneously the custodian's liveness/availability proof (read =
     * audit, R7): the responder must produce the actual bytes, which self-verify by content address.
     */
    fun want(projection: LinkProjection) {
        val line = SyncControlWire.encodeWant(projection)
        for (session in sessions.toList()) session.sendRaw(line)
    }

    private fun receive(from: P2pSession, line: String) {
        if (line.length > ReplicatedEventWireLimits.MAX_LINE_BYTES) {
            log("ReplicatedEventSync: closed session after oversized wire line")
            from.close()
            return
        }
        when (val staged = stagers.getOrPut(from) { codec.Stager() }.accept(line)) {
            is BatchWireCodec.StageResult.Pending -> return
            is BatchWireCodec.StageResult.Complete -> return receiveBatch(from, staged.batch)
            is BatchWireCodec.StageResult.Violation -> {
                log("ReplicatedEventSync: closed session after ${staged.reason}")
                from.close()
                return
            }
            is BatchWireCodec.StageResult.NotBatch -> Unit
        }
        if (BatchWireCodec.isBatchLine(line)) {
            log("ReplicatedEventSync: closed session after data line outside batch")
            from.close()
            return
        }
        if (SyncControlWire.isFrontier(line)) return onFrontier(from, line)
        if (line == SyncControlWire.SYNCED_TAG) return onCaughtUp(from)
        if (line.startsWith(ENVELOPE_TAG)) return receiveEnvelope(from, line)
        if (SyncControlWire.isWant(line)) return onWant(from, line)
        log("ReplicatedEventSync: dropped malformed control line")
    }

    private fun receiveBatch(from: P2pSession, batch: ReplicatedEventBatch) {
        val validation = when (val result = validateReplicatedEventBatch(batch, store)) {
            is BatchValidationResult.Invalid -> {
                log("ReplicatedEventSync: closed session after invalid batch: ${result.reason}")
                from.close()
                return
            }
            is BatchValidationResult.Valid -> result
        }
        // Validation already judged each of these against this store; committing them is insertion, not a second verdict.
        val advanced = commitValidated(validation).isNotEmpty()
        if (advanced) {
            for (session in sessions.toList()) if (session !== from) sendBatch(session, batch)
            onAdvanced()
        }
    }

    /**
     * A directed envelope arrived: drop it if already seen (loop prevention), else forward it onward to every other
     * session **without reading** (relay) and hand it to the owner, who decides whether it is the addressee. The
     * envelope is never stored, so this never advances local state and never triggers [onAdvanced].
     */
    private fun receiveEnvelope(from: P2pSession, line: String) {
        val envelope = DirectedEnvelope.decode(line, scheme) ?: return log("ReplicatedEventSync: dropped malformed envelope")
        if (! markSeenEnvelope(line)) return   // already relayed this exact envelope — break the flood loop
        for (session in sessions.toList()) if (session !== from) session.sendRaw(line)
        onEnvelope(envelope)
    }

    /**
     * Serve a WANT-by-digest pull: send back, on the requesting session only, whatever resolves the requested
     * projection — the plaintext closure if we hold it, and/or any sealed ciphertext we custody for it. Producing the
     * bytes is the liveness proof (R7). A request we cannot serve is silently ignored (best-effort).
     */
    private fun onWant(from: P2pSession, line: String) {
        val wanted = SyncControlWire.decodeWant(line, scheme) ?: return log("ReplicatedEventSync: dropped malformed want")
        val resolver = store.resolver()
        val reachable = linkedSetOf<LinkProjection>()
        // Never serve plaintext for a withheld projection (R2 verify≠read): a sealed/private body the author holds locally
        // stays dark to a peer lacking the key. The author's own store holds the full plaintext closure, so without this
        // guard a direct `WANT(contentProjection)` would walk and return it — the exact capability bypass. The seal below
        // is still served, so the requester can custody the ciphertext; it just cannot read it. `collectReachable` already
        // honours `isWithheld` on the content-leaf indirection; this closes the one gap where the root *is* that content.
        if (! isWithheld(wanted) && resolver.recordFor(wanted) != null) collectReachable(wanted, resolver, reachable)
        val preimages = reachable.mapNotNull { resolver.recordFor(it)?.let(::LinkPreimage) }
        val seals = store.sealedFor(wanted).toList()
        if (preimages.isEmpty() && seals.isEmpty()) return
        for (batch in codec.encode(preimages + seals, resolver)) sendBatch(from, batch)
    }

    /** Record [line] as seen; returns false if it already was. Bounded, oldest-first eviction. */
    private fun markSeenEnvelope(line: String): Boolean {
        if (! seenEnvelopes.add(line)) return false
        if (seenEnvelopes.size > MAX_SEEN_ENVELOPES) seenEnvelopes.iterator().run { next(); remove() }
        return true
    }

    /** A peer advertised its frontier: compute the suffix it lacks relative to ours and push only that. */
    private fun onFrontier(to: P2pSession, line: String) {
        val peer = SyncControlWire.decodeFrontier(line, scheme)
            ?: return log("ReplicatedEventSync: dropped malformed frontier")
        for (batch in computeDeltaBatches(peer)) sendBatch(to, batch)
        // WebSocket/data-channel ordering makes this a barrier for every preceding suffix batch, not a claim that the
        // socket merely opened. The requester marks itself caught up only after consuming this line.
        to.sendRaw(SyncControlWire.SYNCED_TAG)
    }

    /**
     * Everything in our store that [peer] lacks, as preimages (any order — each self-verifies), attestations
     * (oldest-first), and sealed ciphertexts whose content the pushed heads reference. Computed per credential by
     * walking each of our heads back to the peer's known head.
     *
     * Seals ride along with the spine the same way a plaintext content body does (a custodian floods the ciphertext,
     * §20.1). The frontier digest is per-actor heads only, so it cannot tell us which seals the peer already holds; each
     * one therefore rides the batch that first carries its content leaf, and the peer deduplicates by ciphertext digest
     * on apply. A seal under a closure the peer already has is *not* re-offered here — that is the same floor the spine
     * suffix already sits below, and a peer that forgot such a ciphertext recovers it by WANT-by-digest.
     *
     * The whole push is computed as one pass and encoded in one call, rather than a walk-and-encode per commitment.
     * Both halves of that matter, and for the same underlying reason — a commitment's Link *is* its whole ancestry:
     *
     * - [alreadySent] carries across attestations as [collectReachable]'s stop set, so each node in the shared ancestry
     *   is walked once instead of once per descendant;
     * - and the codec shares one materialization memo across everything handed to it, so an ancestor's Link is rebuilt
     *   once. Encoding per commitment made every batch re-resolve the entire chain below it through the resolver.
     *
     * Serving a newcomer is what makes both matter: its frontier is empty, so the suffix is the entire history, and the
     * quadratic version froze a real store's event loop for minutes to send exactly the same bytes. The codec still
     * splits the result on its own wire limits, and still puts at most one commitment in a batch, so a large delta
     * arrives as dependency-only batches followed by the commitments they carry.
     */
    private fun computeDeltaBatches(
        peer: Map<LinkProjection, Set<LinkProjection>>,
    ): List<ReplicatedEventBatch> {
        val resolver = store.resolver()
        val events = mutableListOf<ReplicatedEvent>()
        val alreadySent = mutableSetOf<LinkProjection>()
        val scheduledAttestations = mutableSetOf<LinkProjection>()
        // Indexed once up front: [ReplicatedEventStore.sealedFor] scans every held seal, and the loop below asks per commitment.
        val sealsByContent: Map<LinkProjection, List<SealedContent>> =
            store.sealedContents().groupBy(SealedContent::contentProjection)

        for ((credential, ourHeads) in store.frontier()) {
            val peerHeads = peer[credential].orEmpty()
            for (peerHead in peerHeads) collectReachable(peerHead, resolver, alreadySent)
            for (head in ourHeads) {
                if (head in peerHeads) continue
                val suffix = mutableListOf<SignedCommitment>()
                walkSuffix(head, peerHeads, resolver, suffix)
                for (attestation in suffix) {
                    if (! scheduledAttestations.add(attestation.head)) continue
                    val contentLeaves = linkedSetOf<LinkProjection>()
                    // Oldest-first, so by here the previous commitment's closure is already in [alreadySent] and this
                    // walk stops at its head: what comes back is this commitment's own new nodes, in the same order the
                    // full walk would have yielded them after subtracting what was sent.
                    events += collectReachable(attestation.head, resolver, alreadySent, contentLeaves)
                        .mapNotNull { projection -> resolver.recordFor(projection)?.let(::LinkPreimage) }
                    events += attestation
                    events += contentLeaves.flatMap { sealsByContent[it].orEmpty() }
                }
            }
        }
        return codec.encode(events, resolver)
    }

    /** Walk the commitment chain from [head] toward any [stopAt] head, appending each attestation oldest-first. */
    private fun walkSuffix(
        head: LinkProjection,
        stopAt: Set<LinkProjection>,
        resolver: ProjectedLinkRecordResolver,
        into: MutableList<SignedCommitment>,
    ) {
        val suffix = mutableListOf<SignedCommitment>()
        var current: LinkProjection? = head
        while (current != null && current !in stopAt) {
            val attestation = store.attestation(current) ?: break
            suffix += attestation
            val parent = resolver.recordFor(current)?.parent
            // A parent that still carries an attestation is the previous commitment; otherwise we have reached genesis.
            current = if (parent != null && store.containsAttestation(parent)) parent else null
        }
        into += suffix.asReversed()  // oldest-first keeps the receiver's re-drive churn minimal
    }

    /** Projection of the scheme's protocol link; a record whose parent equals this is a projection-encoding leaf. */
    private val schemeProtocolProjection: LinkProjection by lazy { store.projector.project(scheme.protocolLink) }

    /**
     * Collect the projections of every preimage reachable from [root] that resolves locally, into [acc], and return the
     * ones this call newly added, in walk order; and, when [contentLeaves] is given, record the content projection named
     * by each content-digest leaf newly encountered (held or not) so [computeDelta] can attach any matching sealed
     * ciphertext.
     *
     * [acc] is both the accumulator and the frontier's stop set: anything already in it is skipped **along with
     * everything under it**. That is sound because [acc] only ever gains nodes this walk (or an earlier completed one)
     * walked to completion, so a node present in it has had its whole subtree collected already — which is what turns a
     * chain served commitment-by-commitment from quadratic into linear.
     */
    private fun collectReachable(
        root: LinkProjection,
        resolver: ProjectedLinkRecordResolver,
        acc: MutableSet<LinkProjection>,
        contentLeaves: MutableSet<LinkProjection>? = null,
    ): Set<LinkProjection> {
        // Iterative depth-first, for the same reason [projectedRecords] and [resolveLink] are: this walk follows a
        // commitment's parent edge, so it descends once per link in an actor's history, and a dog-fooding store already
        // nests thousands deep. Recursing here overflowed the native stack *while computing what to send* — so the node
        // never sent, and peers read a live-but-mute body as an empty mesh, with transport, relay, and peer list all
        // reporting healthy. Heap frames remove the depth ceiling; the pre-order below is what preserves semantics.
        val newly = linkedSetOf<LinkProjection>()
        val stack = ArrayDeque<LinkProjection>()
        stack.addLast(root)
        while (stack.isNotEmpty()) {
            val projection = stack.removeLast()
            if (! acc.add(projection)) continue   // seen: its subtree is collected too, so this prunes rather than re-walks
            newly.add(projection)
            val record: ProjectedLinkRecord? = resolver.recordFor(projection)
            // Not held locally: it is not ours to send, and it comes back out of [acc] so a later path may resolve it.
            if (record == null) { acc.remove(projection); newly.remove(projection); continue }
            // Follow a content-address indirection: a projection-encoding leaf (e.g. a commitment's content digest)
            // names a detachable node (its committed content) that is not structurally reachable from the spine but must
            // replicate alongside it. The named content travels either as plaintext preimages *or* as a sealed
            // ciphertext (§20.2): we record the named projection in [contentLeaves] either way, then visit the plaintext
            // only when it is held and not withheld. Withheld plaintext (a private post, §7.1) and never-held dark
            // content stay skipped — the header replicates while the body is a missing observation — but a held seal for
            // that projection is still offered by [computeDelta], which is how dark-but-sealed content gets custodied.
            if (record.parent == schemeProtocolProjection) {
                val embedded = embeddedProjection(projection, resolver)
                if (embedded != null) {
                    contentLeaves?.add(embedded)
                    if (! isWithheld(embedded) && resolver.recordFor(embedded) != null) stack.addLast(embedded)
                }
            }
            // Pushed youngest-first so they pop parent → target → embedded: [acc] is insertion-ordered and its order is
            // the order the receiver re-drives the preimages in, so it has to stay the recursive pre-order exactly.
            record.target?.let(stack::addLast)
            record.parent?.let(stack::addLast)
        }
        return newly
    }

    /** Decode a projection-encoding [leaf] back to the projection it names (whether or not that record is held). */
    private fun embeddedProjection(leaf: LinkProjection, resolver: ProjectedLinkRecordResolver): LinkProjection? {
        val link = (store.projector.resolveLink(leaf, resolver) as? Observation.Known)?.value?.nestedOrNull() ?: return null
        return renderProjection(link)?.takeIf { it.scheme == scheme }
    }

    private fun sendBatch(session: P2pSession, batch: ReplicatedEventBatch) {
        for (wire in batch.wireLines()) session.sendRaw(wire)
    }

    companion object {
        private val ENVELOPE_TAG = DirectedEnvelope.TAG
        /** Upper bound on remembered envelope lines for loop prevention; a re-seen envelope after eviction re-floods once. */
        private const val MAX_SEEN_ENVELOPES = 4096
    }
}
